import { McpServer } from './mcp-server';
import { createSignalRConnection } from './signalr-connection';
const DEVICE_ID_KEY_PREFIX = 'airia-mcp-device-id';
const LEGACY_CLIENT_ID_KEY = 'airia-mcp-client-id';
const HEARTBEAT_INTERVAL_MS = 30_000;
/**
 * Generate or retrieve a persistent device identifier for this host.
 *
 * Format: `office|{hostType}|{uuid}|{uuid}`
 *   - `office`        — origin tag so the hub can distinguish Office add-in connections
 *   - `{hostType}`    — `word` | `excel` | `powerpoint` | `outlook` (the Office app)
 *   - first `{uuid}`  — device/install identifier (stable per browser origin + host)
 *   - second `{uuid}` — reserved for future use (TODO: document purpose once backend schema is finalized)
 *
 * Persisted in localStorage, keyed per host so each Office app gets its own id.
 */
export function getOrCreateDeviceId(hostType) {
    const storageKey = `${DEVICE_ID_KEY_PREFIX}:${hostType}`;
    let deviceId = localStorage.getItem(storageKey);
    if (!deviceId) {
        deviceId = `office|${hostType}|${crypto.randomUUID()}|${crypto.randomUUID()}`;
        localStorage.setItem(storageKey, deviceId);
        // One-shot cleanup: remove the legacy raw-UUID key that predates this format.
        localStorage.removeItem(LEGACY_CLIENT_ID_KEY);
    }
    return deviceId;
}
export class McpSession {
    connection = null;
    hubConnectionState = null;
    mcpServer;
    hostType;
    hubUrl;
    accessTokenFactory;
    _sessionId = null;
    onSessionIdChanged;
    onConnectionStatusChanged;
    isConnected = false;
    isConnecting = false;
    heartbeatTimeout = null;
    heartbeatGeneration = 0;
    constructor(hostType, hubUrl, accessTokenFactory, onSessionIdChanged, onConnectionStatusChanged) {
        this.mcpServer = new McpServer(hostType);
        this.hostType = hostType;
        this.hubUrl = hubUrl;
        this.accessTokenFactory = accessTokenFactory;
        this.onSessionIdChanged = onSessionIdChanged;
        this.onConnectionStatusChanged = onConnectionStatusChanged;
    }
    get sessionId() {
        return this._sessionId;
    }
    async start() {
        if (this.isConnected || this.isConnecting)
            return;
        this.isConnecting = true;
        this.onConnectionStatusChanged?.('connecting');
        try {
            const { connection, HubConnectionState } = createSignalRConnection(this.hubUrl, this.accessTokenFactory);
            this.connection = connection;
            this.hubConnectionState = HubConnectionState;
            this.setupEventHandlers();
            await this.connection.start();
            this.isConnected = true;
            this.isConnecting = false;
            await this.register();
        }
        catch (error) {
            this.isConnecting = false;
            this.connection = null;
            const err = error instanceof Error ? error : new Error(String(error));
            this.onConnectionStatusChanged?.('disconnected', err);
            console.error('[McpSession] Failed to start:', error);
            throw error;
        }
    }
    async stop() {
        if (!this.connection)
            return;
        this.stopHeartbeat();
        try {
            await this.connection.stop();
        }
        catch (error) {
            console.error('[McpSession] Error stopping connection:', error);
        }
        finally {
            this.connection = null;
            this.isConnected = false;
            this.isConnecting = false;
            this.setSessionId(null);
            this.onConnectionStatusChanged?.('disconnected');
        }
    }
    setSessionId(sessionId) {
        this._sessionId = sessionId;
        this.onSessionIdChanged?.(sessionId);
    }
    async register() {
        if (!this.connection ||
            !this.hubConnectionState ||
            this.connection.state !== this.hubConnectionState.Connected) {
            throw new Error('SignalR connection not established');
        }
        try {
            // Pass a stable device identifier; the hub returns the assigned session ID
            const deviceId = getOrCreateDeviceId(this.hostType);
            const sessionId = (await this.connection.invoke('RegisterBrowserAgent', deviceId));
            this.setSessionId(sessionId);
            this.onConnectionStatusChanged?.('connected');
            console.log('[McpSession] Registered with session ID:', sessionId);
            this.startHeartbeat();
        }
        catch (error) {
            console.error('[McpSession] Failed to register:', error);
            throw error;
        }
    }
    startHeartbeat() {
        this.stopHeartbeat();
        // stopHeartbeat() already bumped the generation; capture the current value.
        const gen = this.heartbeatGeneration;
        const tick = async () => {
            if (gen !== this.heartbeatGeneration)
                return;
            if (this.connection &&
                this.hubConnectionState &&
                this.connection.state === this.hubConnectionState.Connected) {
                try {
                    await this.connection.invoke('UpdateLastSeen');
                }
                catch (error) {
                    // Transient errors shouldn't permanently kill the heartbeat.
                    // onreconnecting stops it; onreconnected → register() restarts it.
                    console.error('[McpSession] Heartbeat failed:', error);
                }
            }
            if (gen !== this.heartbeatGeneration)
                return;
            this.heartbeatTimeout = setTimeout(tick, HEARTBEAT_INTERVAL_MS);
        };
        this.heartbeatTimeout = setTimeout(tick, HEARTBEAT_INTERVAL_MS);
    }
    stopHeartbeat() {
        this.heartbeatGeneration++;
        if (this.heartbeatTimeout !== null) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }
    setupEventHandlers() {
        if (!this.connection)
            return;
        this.connection.on('ReceiveMcpMessage', async (...args) => {
            const raw = args[0];
            if (!raw || typeof raw !== 'object' || !('message' in raw) || !('sessionId' in raw)) {
                console.error('[McpSession] Invalid ReceiveMcpMessage payload:', raw);
                return;
            }
            const mcpMessage = raw;
            try {
                const response = await this.mcpServer.processMessage(mcpMessage.message);
                if (response) {
                    await this.sendMcpResponseToSession(mcpMessage.sessionId, mcpMessage.connectionId, mcpMessage.messageId, response);
                }
            }
            catch (error) {
                console.error('[McpSession] Error processing ReceiveMcpMessage:', error);
                const errorResponse = JSON.stringify({
                    jsonrpc: '2.0',
                    id: mcpMessage.messageId,
                    error: {
                        code: -32603,
                        message: 'Internal error processing MCP message',
                        data: error instanceof Error ? error.message : 'Unknown error',
                    },
                });
                await this.sendMcpResponseToSession(mcpMessage.sessionId, mcpMessage.connectionId, mcpMessage.messageId, errorResponse);
            }
        });
        this.connection.on('SendMcpRequest', (...args) => {
            if (typeof args[0] !== 'string') {
                console.error('[McpSession] Invalid SendMcpRequest payload:', args[0]);
                return;
            }
            const mcpMessage = args[0];
            (async () => {
                try {
                    const response = await this.mcpServer.processMessage(mcpMessage);
                    if (response) {
                        await this.connection?.invoke('SendMcpResponse', response);
                    }
                }
                catch (error) {
                    console.error('[McpSession] Error processing SendMcpRequest:', error);
                    let requestId = 'unknown';
                    try {
                        requestId = JSON.parse(mcpMessage).id ?? 'unknown';
                    }
                    catch {
                        // Could not parse message id, use fallback
                    }
                    const errorResponse = JSON.stringify({
                        jsonrpc: '2.0',
                        id: requestId,
                        error: {
                            code: -32603,
                            message: 'Internal error processing MCP message',
                            data: error instanceof Error ? error.message : 'Unknown error',
                        },
                    });
                    await this.connection?.invoke('SendMcpResponse', errorResponse);
                }
            })();
        });
        this.connection.onclose((error) => {
            console.log('[McpSession] Connection closed:', error?.message || 'clean close');
            this.stopHeartbeat();
            this.isConnected = false;
            this.setSessionId(null);
            this.onConnectionStatusChanged?.('disconnected', error ?? null);
        });
        this.connection.onreconnecting((error) => {
            console.log('[McpSession] Reconnecting:', error?.message || 'no error');
            this.stopHeartbeat();
            this.isConnected = false;
            this.setSessionId(null);
            this.onConnectionStatusChanged?.('reconnecting', error ?? null);
        });
        this.connection.onreconnected(async () => {
            console.log('[McpSession] Reconnected');
            this.isConnected = true;
            try {
                await this.register();
            }
            catch (error) {
                console.error('[McpSession] Failed to re-register after reconnect:', error);
            }
        });
    }
    async sendMcpResponseToSession(sessionId, connectionId, messageId, response) {
        if (!this.connection ||
            !this.hubConnectionState ||
            this.connection.state !== this.hubConnectionState.Connected) {
            console.error('[McpSession] Cannot send response: not connected');
            return;
        }
        try {
            await this.connection.invoke('SendMcpResponseToSession', sessionId, connectionId, messageId, response);
        }
        catch (error) {
            console.error('[McpSession] Failed to send MCP response:', error);
        }
    }
}
